/**
 * FilterChips - Mobile
 * 
 * Horizontal scrolling filter chips for property status filtering.
 */

import React from 'react';
import { ScrollView, Pressable, Text, StyleSheet } from 'react-native';
import { color, text, space, radius } from '../../../shared/tokens/mobile';
import { PropertyFilter } from '../../../hooks/usePortfolioProperties';

interface FilterOption {
  key: PropertyFilter;
  label: string;
}

const FILTER_OPTIONS: FilterOption[] = [
  { key: 'all', label: 'All' },
  { key: 'research', label: 'Research' },
  { key: 'under_contract', label: 'Contract' },
  { key: 'owned', label: 'Owned' },
  { key: 'listed_for_sale', label: 'For Sale' },
  { key: 'sold', label: 'Sold' },
];

interface FilterChipsProps {
  activeFilter: PropertyFilter;
  onFilterChange: (filter: PropertyFilter) => void;
  counts: Record<PropertyFilter, number>;
}

export function FilterChips({ activeFilter, onFilterChange, counts }: FilterChipsProps) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.container}
      style={styles.scroll}
    >
      {FILTER_OPTIONS.map((option) => {
        const count = counts[option.key];
        const isActive = activeFilter === option.key;
        
        // Skip filters with zero count (except 'all')
        if (count === 0 && option.key !== 'all') return null;
        
        return (
          <Pressable
            key={option.key}
            onPress={() => onFilterChange(option.key)}
            style={[styles.chip, isActive && styles.chipActive]}
            accessibilityRole="button"
            accessibilityState={{ selected: isActive }}
          >
            <Text style={[styles.chipText, isActive && styles.chipTextActive]}>
              {option.label} ({count})
            </Text>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flexGrow: 0,
  },
  container: {
    paddingHorizontal: space.md,
    paddingVertical: space.md,
    gap: space.sm,
  },
  chip: {
    paddingHorizontal: space.md,
    paddingVertical: space.sm,
    borderRadius: radius.full,
    backgroundColor: color.backgroundSecondary,
    borderWidth: 1,
    borderColor: color.border,
  },
  chipActive: {
    backgroundColor: color.primaryLight,
    borderColor: color.primary,
  },
  chipText: {
    fontSize: text.caption.fontSize,
    fontWeight: '500',
    color: color.textSecondary,
  },
  chipTextActive: {
    color: color.primary,
    fontWeight: '600',
  },
});

